import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@dfh-corp-sites/ui'
import React, { FC, useMemo } from 'react'

import { Metric } from '../../../../graphql/api'
import { SanityParentProductFragment } from '../../../../graphql/gatsby'
import {
  ConfigurableProductDetailsFragment,
  SimpleProductDetailsFragment,
} from '../../../../graphql/magento'
import { Maybe, Money, ProductLabelStatus } from '../../../../graphql/search'
import useUserCategory from '../../../../hooks/useUserCategory'
import { useDispensaryContext } from '../../../../lib/dispensaries'
import { professionalCategories } from '../../../../lib/library'
import { useProductDescription } from '../../../../lib/products/hooks/useProductDescription'
import {
  isSanityImageWithAssetId,
  SanityProductImages,
  SanityProductImagesHierarchy,
} from '../../../../lib/products/images'
import { contentBlocks, dataSheets, displayAttribute } from '../../../../utils/itemTools'
import ItemImages from '../../../products/details/images/ItemImages'
import ItemControlsContainer from '../../../products/details/ItemControlsContainer'
import ItemHeading from '../../../products/details/ItemHeading'
import BlockContainer from '../../../sanity-page/ProductDescription/BlockContainer'
import ProductDescription from '../../../sanity-page/ProductDescription/index'
import DataSheetDisclaimer from '../DataSheetDisclaimer'
import DataSheetLink from '../DataSheetLink'
import DataSheetLinkList from '../DataSheetLinkList'
import { ProtocolsWithProduct } from '../ProtocolsWithProduct'
import ItemAdvisorTags from './ItemAdvisorTags'
import ItemContentBlock from './ItemContentBlock'
import ItemMetric from './ItemMetric'

export interface ItemDetailsProps {
  item: ConfigurableProductDetailsFragment
  onVariantSelected: (itemValue: string) => void
  sanityParentProduct?: SanityParentProductFragment | null
  sanityChildProduct?: NonNullable<SanityParentProductFragment['products']>[number] | null
  selectedVariant: SimpleProductDetailsFragment | null | undefined
  variantLabelStatus: ProductLabelStatus | undefined
  variantRetailPrice: Maybe<Money> | undefined
}

const ItemDetails: FC<ItemDetailsProps> = ({
  item,
  onVariantSelected,
  sanityParentProduct,
  sanityChildProduct,
  selectedVariant = null,
  variantLabelStatus,
  variantRetailPrice,
}) => {
  // Note: the short description is only available on the parent product
  const shortDescription = sanityParentProduct?.shortDescription ?? undefined
  const dispensary = useDispensaryContext()
  const tagline = sanityParentProduct?.tagline ?? undefined

  // collect Sanity images for parent and child products
  const sanityImages = useMemo(
    (): SanityProductImagesHierarchy => ({
      parent: {
        mainImage:
          sanityParentProduct && isSanityImageWithAssetId(sanityParentProduct.image)
            ? {
                ...sanityParentProduct.image,
                // TODO use `alt` from Sanity when parent product schema updated
                alt: '',
              }
            : undefined,
      },
      children: (sanityParentProduct?.products ?? []).reduce((acc, product) => {
        const sku = product?.sku
        if (!sku) {
          return acc
        }

        acc.set(sku, {
          mainImage: isSanityImageWithAssetId(product.mainImage) ? product.mainImage : undefined,
          otherImages: (product.otherImages ?? []).filter(isSanityImageWithAssetId),
        })
        return acc
      }, new Map<string, SanityProductImages>()),
    }),
    [sanityParentProduct],
  )

  const sheets = dataSheets({ item, selectedVariant })
  const { userCategory } = useUserCategory()
  const isProfessional = userCategory && professionalCategories.indexOf(userCategory) > -1

  const jusTheFactsDescription = useProductDescription({
    parentContextualDescription: sanityParentProduct?._rawJustTheFacts?.contextualDescription,
    parentDefaultDescription: sanityParentProduct?._rawJustTheFacts?.defaultDescription,
  })

  const whatSetusApartDescription = useProductDescription({
    parentContextualDescription: sanityParentProduct?._rawWhatSetsUsApart?.contextualDescription,
    parentDefaultDescription: sanityParentProduct?._rawWhatSetsUsApart?.defaultDescription,
  })

  const techSheets = useMemo((): {
    id: string
    text: string
    href: string | null | undefined
  }[] => {
    const sanityTechSheets = sanityParentProduct?.techSheets ?? []
    if (!sanityTechSheets.length && sheets.doc_tech_sheet) {
      return [
        {
          id: sheets.doc_tech_sheet?.id ?? '',
          text: sheets.doc_tech_sheet?.text ?? '',
          href: sheets.doc_tech_sheet?.href ?? null,
        },
      ]
    }

    return (
      sanityTechSheets?.map((techSheet) => ({
        id: techSheet?.id ?? '',
        text: techSheet?.title ?? '',
        href: techSheet?.asset?.asset?.url ?? null,
      })) ?? []
    )
  }, [sanityParentProduct, sheets?.doc_tech_sheet])

  const patientEducation = useMemo((): {
    id: string
    text: string
    href: string | null | undefined
  }[] => {
    const sanityPatientEducation = sanityParentProduct?.patientEducation ?? []
    if (!sanityPatientEducation.length && sheets.doc_patient_education) {
      return [
        {
          id: sheets.doc_patient_education?.id ?? '',
          text: sheets.doc_patient_education?.text ?? '',
          href: sheets.doc_patient_education?.href ?? null,
        },
      ]
    }
    return (
      sanityPatientEducation?.map((pt) => ({
        id: pt?.id ?? '',
        text: pt?.title ?? '',
        href: pt?.asset?.asset?.url ?? null,
      })) ?? []
    )
  }, [sanityParentProduct?.patientEducation, sheets?.doc_patient_education])

  return (
    <div className="divide-y divide-brand-rule-grey md:grid grid-cols-2 md:grid-rows-[repeat(5,auto)]">
      <ItemHeading
        className="px-2.5 md:px-5 xl:px-10 pt-6 pb-5 xl:pb-10 md:col-start-1 md:col-end-1 md:border-r md:border-brand-rule-grey"
        item={item}
        sanityParentProduct={sanityParentProduct}
        selectedVariant={selectedVariant}
        retailPrice={variantRetailPrice}
      />
      <div className="md:col-start-2 md:col-end-2 md:row-start-1 md:row-end-5 md:sticky md:top-0 md:self-start md:!border-t-0">
        <ItemImages
          size="lg"
          previewClassName="px-2.5 md:pr-5 xl:pr-10 justify-end pb-5"
          item={item}
          selectedVariant={selectedVariant}
          sanityImages={sanityImages}
        />
      </div>

      <div className="px-2.5 md:px-5 xl:px-10 py-5 xl:py-6 flex-1 space-y-5 md:col-start-1 md:col-end-1 md:border-r">
        {tagline && <h3 className="font-medium">{tagline}</h3>}
        {shortDescription && <p>{shortDescription}</p>}

        <ItemAdvisorTags item={item} selectedVariant={selectedVariant} />
        <DataSheetDisclaimer />
      </div>
      <ItemControlsContainer
        className="md:col-start-1 md:col-end-1 md:border-r"
        containerType="page"
        item={item}
        sanityParentProduct={sanityParentProduct}
        onVariantSelected={onVariantSelected}
        selectedVariant={selectedVariant}
        variantLabelStatus={variantLabelStatus}
        analyticsEventAppContext={dispensary ? 'dispensary_pdp' : 'pdp'}
      />
      <Accordion
        type="multiple"
        className="md:col-start-1 md:col-end-1 md:border-r border-brand-rule-grey"
        defaultValue={[
          'about-this-product',
          'just-the-facts',
          'what-sets-us-apart',
          'protocol-list',
          'clinical-research',
        ]}
      >
        <AccordionItem
          className="px-2.5 md:px-5 xl:px-10 last:border-b-0 border-brand-rule-grey"
          value="about-this-product"
        >
          <AccordionTrigger iconType="plusMinus" className="text-2xl">
            Benefits
          </AccordionTrigger>
          <AccordionContent>
            <ProductDescription
              item={item}
              selectedVariant={selectedVariant}
              sanityParentProduct={sanityParentProduct}
              sanityChildProduct={sanityChildProduct}
            />
            {patientEducation.length > 1 ? (
              <DataSheetLinkList
                dataSheets={patientEducation}
                isProfessional={isProfessional}
                title="Patient Education"
              />
            ) : (
              <DataSheetLink {...patientEducation[0]} isProfessional={isProfessional} />
            )}
          </AccordionContent>
        </AccordionItem>
        {(jusTheFactsDescription || (isProfessional && techSheets.length)) && (
          <AccordionItem
            className="px-2.5 md:px-5 xl:px-10 last:border-b-0 border-brand-rule-grey"
            value="just-the-facts"
          >
            <AccordionTrigger iconType="plusMinus" className="text-2xl">
              Just the facts
            </AccordionTrigger>
            <AccordionContent>
              <BlockContainer content={jusTheFactsDescription} />
              <section className="flex flex-col xl:flex-row xl:justify-between gap-6 xl:gap-5 mt-5 xl:mt-10">
                {(sanityParentProduct?._rawJustTheFacts?.metrics as Metric[])?.map((metric) => (
                  <ItemMetric className="flex-1" key={metric._key} {...metric} />
                ))}
              </section>
              {techSheets.length > 1 ? (
                <DataSheetLinkList
                  dataSheets={techSheets}
                  isProfessional={isProfessional}
                  title="Tech sheets"
                />
              ) : (
                <DataSheetLink {...techSheets[0]} isProfessional={isProfessional} />
              )}
            </AccordionContent>
          </AccordionItem>
        )}
        {whatSetusApartDescription && (
          <AccordionItem
            className="px-2.5 md:px-5 xl:px-10 last:border-b-0 border-brand-rule-grey"
            value="what-sets-us-apart"
          >
            <AccordionTrigger iconType="plusMinus" className="text-2xl">
              Supplement Facts
            </AccordionTrigger>
            <AccordionContent>
              <BlockContainer content={whatSetusApartDescription} />

              <section className="flex flex-col xl:flex-row xl:justify-between gap-6 xl:gap-5 mt-5 xl:mt-10">
                {(sanityParentProduct?._rawWhatSetsUsApart?.metrics as Metric[])?.map((metric) => (
                  <ItemMetric className="flex-1" key={metric._key} {...metric} />
                ))}
              </section>
            </AccordionContent>
          </AccordionItem>
        )}

        <ProtocolsWithProduct selectedVariant={selectedVariant} />

        {sheets.doc_clinical_trials?.href && (
          <AccordionItem
            className="px-2.5 md:px-5 xl:px-10 last:border-b-0 border-brand-rule-grey"
            value="clinical-research"
          >
            <AccordionTrigger iconType="plusMinus" className="text-2xl">
              Clinical Research
            </AccordionTrigger>
            <AccordionContent>
              <DataSheetLink {...sheets.doc_clinical_trials} isProfessional={isProfessional} />
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
      <div className="divide-y divide-brand-rule-grey !border-t-0 md:col-start-1 md:col-end-1 md:border-r">
        {contentBlocks.map((content) => (
          <ItemContentBlock
            className="px-2.5 md:px-5 xl:px-10"
            key={content}
            content={displayAttribute({ item, selectedVariant, attribute: content })}
          />
        ))}
      </div>
    </div>
  )
}

export default ItemDetails
